import React, { useState, useCallback } from 'react';

const fmtCur = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 });
};
const fmtDt = (v) => {
  if (!v) return '-';
  const d = new Date(v);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const tierColors = {
  PLATINUM: { bg: '#f3e8ff', text: '#6b21a8', border: '#c084fc' },
  GOLD:     { bg: '#fef9c3', text: '#a16207', border: '#facc15' },
  SILVER:   { bg: 'var(--theme-border-light)', text: '#475569', border: 'var(--theme-text-muted)' },
  BRONZE:   { bg: '#fef3c7', text: '#92400e', border: '#f59e0b' },
  NEW:      { bg: '#eff6ff', text: '#1d4ed8', border: '#60a5fa' },
};
const priorityColors = { HIGH: 'var(--theme-danger)', MEDIUM: '#ca8a04', LOW: 'var(--theme-success)' };
const noteCategories = ['GENERAL', 'PREFERENCE', 'COMPLAINT', 'VIP', 'ALERT'];
const noteCategoryColors = {
  GENERAL: '#6b7280', PREFERENCE: '#2563eb', COMPLAINT: 'var(--theme-danger)', VIP: '#7c3aed', ALERT: '#ea580c'
};

function PmsGuestRecognition({ token, selectedProperty }) {
  const [searchType, setSearchType] = useState('mobile');
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [profile, setProfile] = useState(null);
  const [notes, setNotes] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [newNote, setNewNote] = useState('');
  const [newNoteCategory, setNewNoteCategory] = useState('GENERAL');
  const [addingNote, setAddingNote] = useState(false);
  const [lastGenResult, setLastGenResult] = useState(null);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  /* ─── Search ─── */
  const searchGuest = async () => {
    if (!searchValue.trim()) return;
    setSearching(true); setError(''); setSearchResults([]);
    try {
      const param = searchType === 'mobile' ? `mobile=${encodeURIComponent(searchValue)}`
        : searchType === 'email' ? `email=${encodeURIComponent(searchValue)}`
        : `name=${encodeURIComponent(searchValue)}`;
      const res = await fetch(`/api/v1/ai/guest-recognition/lookup?${param}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Search failed');
      if (!data.found) setError('No guests found');
      else setSearchResults(data.guests || []);
    } catch (err) { setError(err.message); }
    finally { setSearching(false); }
  };

  /* ─── Load Profile ─── */
  const loadProfile = async (guestId) => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`/api/v1/ai/guest-recognition/profile/${guestId}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load');
      setProfile(data.profile);
      setNotes(data.notes || []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const selectGuest = (guest) => {
    setSelectedGuest(guest);
    setSearchResults([]);
    setActiveTab('overview');
    setLastGenResult(null);
    setMessage('');
    loadProfile(guest.guest_id);
  };

  /* ─── Generate Intelligence ─── */
  const generateIntelligence = async () => {
    if (!selectedGuest) return;
    setGenerating(true); setError(''); setMessage(''); setLastGenResult(null);
    try {
      const res = await fetch('/api/v1/ai/guest-recognition/generate', {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ guest_id: selectedGuest.guest_id })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Generation failed');
      setLastGenResult(data);
      if (data.cached) {
        setMessage('Profile is up to date — no data changes detected');
      } else {
        const tokens = (Number(data.usage?.input_tokens) || 0) + (Number(data.usage?.output_tokens) || 0);
        setMessage(`Profile generated in ${data.response_time_ms}ms (${tokens.toLocaleString('en-IN')} tokens)`);
      }
      setProfile(data.profile);
      // Reload notes too
      const notesRes = await fetch(`/api/v1/ai/guest-recognition/notes/${selectedGuest.guest_id}`, { headers: headers() });
      const notesData = await notesRes.json();
      setNotes(notesData.notes || []);
    } catch (err) { setError(err.message); }
    finally { setGenerating(false); }
  };

  /* ─── Notes ─── */
  const addNote = async () => {
    if (!newNote.trim() || !selectedGuest) return;
    setAddingNote(true);
    try {
      const res = await fetch('/api/v1/ai/guest-recognition/notes', {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ guest_id: selectedGuest.guest_id, note_text: newNote, category: newNoteCategory })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add note');
      setNotes(prev => [data.note, ...prev]);
      setNewNote(''); setNewNoteCategory('GENERAL');
    } catch (err) { setError(err.message); }
    finally { setAddingNote(false); }
  };

  const deleteNote = async (noteId) => {
    try {
      const res = await fetch(`/api/v1/ai/guest-recognition/notes/${noteId}`, { method: 'DELETE', headers: headers() });
      if (!res.ok) throw new Error('Failed to delete');
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (err) { setError(err.message); }
  };

  /* ─── Styles ─── */
  const s = {
    container: { padding: 24, maxWidth: 1200, margin: '0 auto' },
    title: { fontSize: 22, fontWeight: 700, marginBottom: 20, color: '#1e293b' },
    card: { background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,.08)', padding: 20, marginBottom: 16 },
    searchRow: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
    select: { padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, background: '#fff' },
    input: { padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 14, flex: 1, minWidth: 200 },
    btnPrimary: { padding: '8px 20px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 14 },
    btnSecondary: { padding: '6px 14px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: 'var(--theme-text)', cursor: 'pointer', fontSize: 13 },
    btnDanger: { padding: '4px 10px', borderRadius: 4, border: 'none', background: '#fef2f2', color: 'var(--theme-danger)', cursor: 'pointer', fontSize: 12 },
    btnSuccess: { padding: '8px 20px', borderRadius: 6, border: 'none', background: '#7c3aed', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 14 },
    error: { background: '#fef2f2', color: 'var(--theme-danger)', padding: '10px 16px', borderRadius: 8, marginBottom: 12, fontSize: 14 },
    success: { background: '#f0fdf4', color: 'var(--theme-success)', padding: '10px 16px', borderRadius: 8, marginBottom: 12, fontSize: 14 },
    info: { background: '#eff6ff', color: '#1d4ed8', padding: '10px 16px', borderRadius: 8, marginBottom: 12, fontSize: 14 },
    badge: (bg, color, border) => ({ display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700, background: bg, color, border: `1px solid ${border || bg}` }),
    tabRow: { display: 'flex', gap: 0, borderBottom: '2px solid var(--theme-border)', marginBottom: 16 },
    tab: (active) => ({ padding: '10px 20px', cursor: 'pointer', fontWeight: active ? 700 : 400, color: active ? '#2563eb' : 'var(--theme-text-secondary)', borderBottom: active ? '2px solid #2563eb' : '2px solid transparent', marginBottom: -2, fontSize: 14, background: 'none', border: 'none', borderBottomWidth: 2, borderBottomStyle: 'solid', borderBottomColor: active ? '#2563eb' : 'transparent' }),
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
    th: { textAlign: 'left', padding: '10px 12px', background: 'var(--theme-bg-light)', borderBottom: '1px solid var(--theme-border)', fontWeight: 600, color: '#475569', fontSize: 13 },
    td: { padding: '10px 12px', borderBottom: '1px solid var(--theme-border-light)', verticalAlign: 'top' },
    profileHeader: { display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginBottom: 16 },
    summaryBox: { background: 'var(--theme-bg-light)', borderRadius: 8, padding: 16, borderLeft: '4px solid #7c3aed', marginBottom: 16, lineHeight: 1.6, color: '#334155', fontSize: 14, fontStyle: 'italic' },
    kpiRow: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 },
    kpi: (color) => ({ flex: '1 1 150px', background: '#fff', borderRadius: 8, padding: 16, borderLeft: `4px solid ${color}`, boxShadow: '0 1px 3px rgba(0,0,0,.06)' }),
    kpiLabel: { fontSize: 12, color: 'var(--theme-text-secondary)', marginBottom: 4 },
    kpiValue: { fontSize: 20, fontWeight: 700 },
    recCard: (priority) => ({ background: '#fff', border: `1px solid ${priority === 'HIGH' ? '#fecaca' : priority === 'MEDIUM' ? '#fde68a' : '#d1fae5'}`, borderRadius: 8, padding: 12, marginBottom: 8 }),
  };

  /* ─── Render ─── */
  const tier = profile?.loyalty_tier || 'NEW';
  const tc = tierColors[tier] || tierColors.NEW;
  const spending = profile?.spending_summary || {};
  const patterns = profile?.service_patterns || {};
  const complaints = profile?.past_complaints || [];
  const occasions = profile?.special_occasions || [];
  const recommendations = profile?.ai_recommendations || [];

  return (
    <div style={s.container}>
      <div style={s.title}>Smart Guest Recognition & Personalization</div>

      {/* Search */}
      <div style={s.card}>
        <div style={{ fontWeight: 600, marginBottom: 10, color: '#334155' }}>Find Guest</div>
        <div style={s.searchRow}>
          <select value={searchType} onChange={e => setSearchType(e.target.value)} style={s.select}>
            <option value="mobile">Mobile</option>
            <option value="email">Email</option>
            <option value="name">Name</option>
          </select>
          <input
            style={s.input}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && searchGuest()}
            placeholder={searchType === 'mobile' ? 'Enter mobile number' : searchType === 'email' ? 'Enter email address' : 'Enter guest name'}
          />
          <button style={s.btnPrimary} onClick={searchGuest} disabled={searching}>
            {searching ? 'Searching...' : 'Search'}
          </button>
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Guest Name</th>
                  <th style={s.th}>Mobile</th>
                  <th style={s.th}>Email</th>
                  <th style={s.th}>AI Profile</th>
                  <th style={s.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map(g => (
                  <tr key={g.guest_id} style={{ cursor: 'pointer' }} onClick={() => selectGuest(g)}>
                    <td style={s.td}>{g.guest_name || '-'}</td>
                    <td style={s.td}>{g.mobile_number || '-'}</td>
                    <td style={s.td}>{g.email || '-'}</td>
                    <td style={s.td}>
                      {g.has_ai_profile
                        ? <span style={s.badge('#f0fdf4', 'var(--theme-success)', '#bbf7d0')}>Available</span>
                        : <span style={s.badge('var(--theme-border-light)', 'var(--theme-text-secondary)', '#cbd5e1')}>Not generated</span>}
                    </td>
                    <td style={s.td}>
                      <button style={s.btnSecondary} onClick={(e) => { e.stopPropagation(); selectGuest(g); }}>View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {error && <div style={s.error}>{error}</div>}
      {message && <div style={s.info}>{message}</div>}

      {/* Selected Guest Profile */}
      {selectedGuest && (
        <div style={s.card}>
          <div style={s.profileHeader}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
                <span style={{ fontSize: 20, fontWeight: 700, color: '#1e293b' }}>{selectedGuest.guest_name || 'Guest'}</span>
                {profile && <span style={s.badge(tc.bg, tc.text, tc.border)}>{tier}</span>}
              </div>
              <div style={{ fontSize: 13, color: 'var(--theme-text-secondary)' }}>
                {selectedGuest.mobile_number && <span style={{ marginRight: 16 }}>Mobile: {selectedGuest.mobile_number}</span>}
                {selectedGuest.email && <span>Email: {selectedGuest.email}</span>}
              </div>
              {profile && (
                <div style={{ fontSize: 12, color: 'var(--theme-text-muted)', marginTop: 4 }}>
                  Profile last updated: {fmtDt(profile.updated_at)}
                </div>
              )}
            </div>
            <button style={s.btnSuccess} onClick={generateIntelligence} disabled={generating}>
              {generating ? 'Generating...' : profile ? 'Refresh Intelligence' : 'Generate Intelligence'}
            </button>
          </div>

          {loading && <div style={{ textAlign: 'center', padding: 20, color: 'var(--theme-text-secondary)' }}>Loading profile...</div>}

          {!loading && !profile && !generating && (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--theme-text-muted)' }}>
              No AI profile generated yet. Click "Generate Intelligence" to analyze this guest's history.
            </div>
          )}

          {!loading && profile && (
            <>
              {/* Profile Summary */}
              {profile.guest_profile_summary && (
                <div style={s.summaryBox}>{profile.guest_profile_summary}</div>
              )}

              {/* KPI Row */}
              <div style={s.kpiRow}>
                <div style={s.kpi('#2563eb')}>
                  <div style={s.kpiLabel}>Total Stays</div>
                  <div style={{ ...s.kpiValue, color: '#2563eb' }}>{profile.total_stays || 0}</div>
                </div>
                <div style={s.kpi('#7c3aed')}>
                  <div style={s.kpiLabel}>Lifetime Value</div>
                  <div style={{ ...s.kpiValue, color: '#7c3aed' }}>{fmtCur(spending.total_lifetime_value)}</div>
                </div>
                <div style={s.kpi('#059669')}>
                  <div style={s.kpiLabel}>Avg / Stay</div>
                  <div style={{ ...s.kpiValue, color: '#059669' }}>{fmtCur(spending.avg_per_stay)}</div>
                </div>
                <div style={s.kpi('#ca8a04')}>
                  <div style={s.kpiLabel}>Preferred Room</div>
                  <div style={{ ...s.kpiValue, color: '#ca8a04', fontSize: 16 }}>{profile.preferred_room_type || '-'}</div>
                </div>
              </div>

              {/* Tabs */}
              <div style={s.tabRow}>
                {['overview', 'spending', 'preferences', 'history', 'notes'].map(tab => (
                  <button key={tab} style={s.tab(activeTab === tab)} onClick={() => setActiveTab(tab)}>
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              {activeTab === 'overview' && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 12, color: '#334155' }}>AI Recommendations</div>
                  {recommendations.length === 0 && <div style={{ color: 'var(--theme-text-muted)', padding: 8 }}>No recommendations available</div>}
                  {recommendations.map((rec, i) => (
                    <div key={i} style={s.recCard(rec.priority)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={s.badge(
                          rec.priority === 'HIGH' ? '#fef2f2' : rec.priority === 'MEDIUM' ? '#fffbeb' : '#f0fdf4',
                          priorityColors[rec.priority] || '#6b7280'
                        )}>{rec.priority}</span>
                        {rec.category && <span style={s.badge('var(--theme-border-light)', '#475569', '#cbd5e1')}>{rec.category}</span>}
                      </div>
                      <div style={{ color: '#334155', fontSize: 14 }}>{rec.recommendation}</div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'spending' && (
                <div>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>Metric</th>
                        <th style={s.th}>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr><td style={s.td}>Total Lifetime Value</td><td style={s.td}><strong>{fmtCur(spending.total_lifetime_value)}</strong></td></tr>
                      <tr><td style={s.td}>Average per Stay</td><td style={s.td}>{fmtCur(spending.avg_per_stay)}</td></tr>
                      <tr><td style={s.td}>Highest Category</td><td style={s.td}>{spending.highest_category || '-'}</td></tr>
                      <tr><td style={s.td}>F&B Percentage</td><td style={s.td}>{spending.fnb_percentage != null ? `${spending.fnb_percentage}%` : '-'}</td></tr>
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === 'preferences' && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 8, color: '#334155' }}>Preferences</div>
                  {(patterns.preferences || []).length === 0 && <div style={{ color: 'var(--theme-text-muted)', padding: 8 }}>No preferences identified</div>}
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {(patterns.preferences || []).map((p, i) => <li key={i} style={{ marginBottom: 6, color: '#334155' }}>{p}</li>)}
                  </ul>

                  <div style={{ fontWeight: 600, marginTop: 16, marginBottom: 8, color: '#334155' }}>Habits</div>
                  {(patterns.habits || []).length === 0 && <div style={{ color: 'var(--theme-text-muted)', padding: 8 }}>No habits identified</div>}
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {(patterns.habits || []).map((h, i) => <li key={i} style={{ marginBottom: 6, color: '#334155' }}>{h}</li>)}
                  </ul>
                </div>
              )}

              {activeTab === 'history' && (
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 8, color: '#334155' }}>Past Complaints</div>
                  {complaints.length === 0 && <div style={{ color: 'var(--theme-text-muted)', padding: 8 }}>No complaints on record</div>}
                  {complaints.length > 0 && (
                    <table style={s.table}>
                      <thead>
                        <tr>
                          <th style={s.th}>Complaint</th>
                          <th style={s.th}>Date</th>
                          <th style={s.th}>Severity</th>
                          <th style={s.th}>Recommendation</th>
                        </tr>
                      </thead>
                      <tbody>
                        {complaints.map((c, i) => (
                          <tr key={i}>
                            <td style={s.td}>{c.complaint}</td>
                            <td style={s.td}>{c.date || '-'}</td>
                            <td style={s.td}><span style={s.badge(
                              c.severity === 'HIGH' ? '#fef2f2' : c.severity === 'MEDIUM' ? '#fffbeb' : '#f0fdf4',
                              priorityColors[c.severity] || '#6b7280'
                            )}>{c.severity || '-'}</span></td>
                            <td style={s.td}>{c.recommendation || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  <div style={{ fontWeight: 600, marginTop: 20, marginBottom: 8, color: '#334155' }}>Special Occasions</div>
                  {occasions.length === 0 && <div style={{ color: 'var(--theme-text-muted)', padding: 8 }}>No special occasions detected</div>}
                  {occasions.length > 0 && (
                    <table style={s.table}>
                      <thead>
                        <tr><th style={s.th}>Type</th><th style={s.th}>Date</th></tr>
                      </thead>
                      <tbody>
                        {occasions.map((o, i) => (
                          <tr key={i}><td style={s.td}>{o.type}</td><td style={s.td}>{o.date || '-'}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {activeTab === 'notes' && (
                <div>
                  {/* Add Note */}
                  <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)', marginBottom: 4 }}>Note</div>
                      <input
                        style={{ ...s.input, width: '100%' }}
                        value={newNote}
                        onChange={e => setNewNote(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && addNote()}
                        placeholder="Add a staff note about this guest..."
                      />
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)', marginBottom: 4 }}>Category</div>
                      <select value={newNoteCategory} onChange={e => setNewNoteCategory(e.target.value)} style={s.select}>
                        {noteCategories.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <button style={s.btnPrimary} onClick={addNote} disabled={addingNote || !newNote.trim()}>
                      {addingNote ? 'Adding...' : 'Add Note'}
                    </button>
                  </div>

                  {/* Notes List */}
                  {notes.length === 0 && <div style={{ color: 'var(--theme-text-muted)', padding: 8 }}>No staff notes yet</div>}
                  {notes.map(n => (
                    <div key={n.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--theme-border-light)' }}>
                      <span style={s.badge(
                        noteCategoryColors[n.category] ? noteCategoryColors[n.category] + '18' : 'var(--theme-border-light)',
                        noteCategoryColors[n.category] || '#6b7280'
                      )}>{n.category}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#334155', fontSize: 14 }}>{n.note_text}</div>
                        <div style={{ fontSize: 12, color: 'var(--theme-text-muted)', marginTop: 2 }}>
                          {n.created_by && <span>{n.created_by} · </span>}
                          {fmtDt(n.created_at)}
                        </div>
                      </div>
                      <button style={s.btnDanger} onClick={() => deleteNote(n.id)} title="Delete note">Delete</button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default PmsGuestRecognition;
